import { Injectable } from '@angular/core';
import { IAffCodeService } from './iaffcode.service';
import { ApiService, UtilService, ApiCacheOptions } from '../../shared/index';
import { ProfileServiceApiService } from 'src/app/shared/api/profile-service-api.service';
import { AffCode } from './affcode.model';
import { AffCodeType } from './affcode-type.model';
import { PagedResultGeneric } from './../../shared/model/paged-result.model';
import { ProfileAdvancedSearchParams } from 'src/app/profiles/search/profile-advanced-search-params.model';
import { AffCodesForProfileDto } from './dto/affcodes-for-profile.dto';
import { AffiliationSearchDto } from './dto/affiliation-search.dto';
import { AffiliationRelatedUpdateDto } from './dto/affiliation-related-update.dto';
import { AffiliationRelatedDto } from './dto/affiliation-related.dto';

@Injectable({ providedIn: 'root' })
export class AffCodeService implements IAffCodeService {
  public _cacheOptions: ApiCacheOptions;

  public constructor(
    private _apiService: ApiService,
    private _profileServiceApiService: ProfileServiceApiService,
    private _utilService: UtilService
  ) {
    this._cacheOptions = this._apiService.getDefaultApiCacheOptionsForInfrequentlyChangedData();
  }

  public search(searchDto: AffiliationSearchDto, forceRefresh?: boolean): Promise<PagedResultGeneric<AffCode>> {
    let cacheOptions = null;
    if (forceRefresh) {
      cacheOptions = this._apiService.getDefaultApiCacheOptionsForNoCache();
    }
    return this._apiService.getPaged('Affiliations', searchDto, cacheOptions);
  }

  public get(id: number): Promise<AffCode> {
    return this._apiService.get(`Affiliation/${id}`);
  }

  public create(dto: AffCode): Promise<number> {
    return this._apiService.post('Affiliations', dto);
  }

  public update(dto: AffCode): Promise<void> {
    return this._apiService.put(`Affiliation/${dto.AffId}`, dto);
  }

  public delete(id: number): Promise<void> {
    return this._apiService.delete(`Affiliation/${id}`);
  }

  public getRelatedAffiliationRecords(id: number): Promise<AffiliationRelatedDto[]> {
    return this._apiService.get(`Affiliation/${id}/Related`, this._apiService.getDefaultApiCacheOptionsForNoCache());
  }

  public updateRelatedAffiliations(dto: AffiliationRelatedUpdateDto): Promise<void> {
    return this._apiService.post('Affiliations/UpdateRelations', dto);
  }

  public getUserDefinedAffCodes = (): Promise<Array<AffCode>> => {
    const url = 'Affiliations/UserDefinedAffiliations';

    return this._apiService.get(url, this._cacheOptions);
  };

  public getUserDefinedAffCodeTypesAndAffCodes = (): Promise<Array<AffCodeType>> => {
    const url = 'Affiliations/UserDefinedAffiliations';

    return this._apiService.get(url, this._cacheOptions).then((data: Array<AffCode>) => {
      const affCodeTypes = new Array<AffCodeType>();
      const affCodeTypeNone = new AffCodeType();
      affCodeTypeNone.AffCodeType = '';
      affCodeTypeNone.AffCodeTypeID = 0;
      affCodeTypes.push(affCodeTypeNone);

      for (const affCode of data) {
        if (this._utilService.isNullOrUndefined(affCode.AffCodeType)) {
          affCode.AffCodeType = '';
          affCode.AffCodeTypeId = 0;
        }

        if (!this._utilService.arrayContainsEntry(affCodeTypes, 'AffCodeTypeID', affCode.AffCodeTypeId)) {
          const affCodeType = new AffCodeType();
          affCodeType.AffCodeType = affCode.AffCodeType;
          affCodeType.AffCodeTypeID = affCode.AffCodeTypeId;
          affCodeTypes.push(affCodeType);
        }
      }

      //now set the aff codes as children under these
      for (const act of affCodeTypes) {
        for (const ac of data) {
          if (ac.AffCodeTypeId == act.AffCodeTypeID) {
            act.AffCodes.push(ac);
          }
        }
      }

      return Promise.resolve(affCodeTypes);
    });
  };

  public getAllAffCodeTypesAndAffCodes(includeChildIds?: boolean): Promise<Array<AffCodeType>> {
    let url = 'Affiliations';
    if (includeChildIds) {
      url = `Affiliations?IncludeChildAffIds=true&PageSize=0`;
    } else {
      url = `Affiliations?PageSize=0`;
    }

    return this._apiService.get(url, this._cacheOptions).then((result: PagedResultGeneric<AffCode>) => {
      const data: Array<AffCode> = result.Result;
      const affCodeTypes = new Array<AffCodeType>();
      const affCodeTypeNone = new AffCodeType();
      affCodeTypeNone.AffCodeType = '';
      affCodeTypeNone.AffCodeTypeID = 0;
      affCodeTypes.push(affCodeTypeNone);

      for (const affCode of data) {
        if (this._utilService.isNullOrUndefined(affCode.AffCodeType)) {
          affCode.AffCodeType = '';
          affCode.AffCodeTypeId = 0;
        }

        if (!affCodeTypes.some((x) => x.AffCodeTypeID == affCode.AffCodeTypeId)) {
          const affCodeType = new AffCodeType();
          affCodeType.AffCodeType = affCode.AffCodeType;
          affCodeType.AffCodeTypeID = affCode.AffCodeTypeId;
          affCodeTypes.push(affCodeType);
        }
      }

      //now set the aff codes as children under these
      for (const act of affCodeTypes) {
        for (const ac of data) {
          if (ac.AffCodeTypeId == act.AffCodeTypeID) {
            act.AffCodes.push(ac);
          }
        }
      }

      return Promise.resolve(affCodeTypes);
    });
  }

  public addAffCodeToProfiles = (affCode: string, profileIds: Array<number>): Promise<void> => {
    const url = 'profile/v2/affiliation/add-affcode-to-profiles?affCode=' + encodeURIComponent(affCode);
    return this._profileServiceApiService.post(url, profileIds);
  };

  public massAddAffCodeToProfiles(affId: number, searchParams: ProfileAdvancedSearchParams) {
    const url = `profile/v2/affiliation/${affId}/mass-add-affcode-to-profiles`;
    return this._profileServiceApiService.post(url, searchParams);
  }

  public removeAffCodeFromProfiles = (affCode: string, profileIds: Array<number>): Promise<void> => {
    const url = `profile/v2/affiliation/remove-affcode-from-profiles?affCode=${encodeURIComponent(affCode)}`;
    return this._profileServiceApiService.post(url, profileIds);
  };

  public massRemoveAffCodeFromProfiles(affId: number, searchParams: ProfileAdvancedSearchParams) {
    const url = `profile/v2/affiliation/${affId}/mass-remove-affcode-from-profiles`;
    return this._profileServiceApiService.post(url, searchParams);
  }

  public addAffCodesToProfile(profileId, affCodes: Array<string>) {
    const url = `Affiliations/AddAffiliationsToProfile`;
    const dto = {
      ProfileID: parseInt(profileId, 10),
      AffCodes: affCodes,
    } as AffCodesForProfileDto;
    return this._apiService.post(url, dto);
  }

  public removeAffCodesForProfile(profileId, affCodes: Array<string>) {
    const url = 'Affiliations/RemoveAffiliationsForProfile';
    const dto = {
      ProfileID: parseInt(profileId, 10),
      AffCodes: affCodes,
    } as AffCodesForProfileDto;
    return this._apiService.post(url, dto);
  }

  public getAllAffCodes(): Promise<Array<AffCode>> {
    const url = 'Affiliations?PageNumber=0&PageSize=0';
    return this._apiService.get(url, this._cacheOptions).then((data: PagedResultGeneric<AffCode>) => {
      return data?.Result;
    });
  }

  public removeProfileFromEntity(profileId: number, settingBatch?: string) {
    let url = `Membership/${profileId}/Entity`;
    if (settingBatch) {
      url += '?settingBatch=' + encodeURIComponent(settingBatch);
    }
    return this._apiService.delete(url);
  }
}
